import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { AuthService, extractLoginToken, isLoginTwoFactorRequired } from "@/services/auth.service";
import { IAuthResponse } from "./types";
import { useCookies } from "react-cookie";
import { useQueryClient } from "@tanstack/react-query";
import { IUser } from "@/@types/User";
import { setAuthToken } from "@/services/api";
import { RegisterService } from "@/services/register.service";
import useModalStore from "@/store/useModalStore";

interface AuthContextDataProps {
  authData?: IUser;
  setAuthData: React.Dispatch<React.SetStateAction<IUser | undefined>>;
  fetchUserData: () => Promise<void>;
  signIn: (
    email: string,
    password: string
  ) => Promise<IAuthResponse | undefined>;
  signInAccount: (
    email: string,
    password: string,
    options?: { challenge_id?: string; code?: string },
  ) => Promise<any>;
  signUp(data: {
    terms: boolean;
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    phone: string;
    phone_prefix: string;
    language_preference?: string;
    invite_token?: string;
  }, referralCode?: string): Promise<IAuthResponse | undefined>;
  signOut: () => Promise<void>;
  replaceSession: (token: string) => Promise<void>;
  switchStore: (membershipUuid: string) => Promise<void>;
  loading: boolean;
  token: string;
  saveToken: (token: string) => void;
  saveEmail: (email: string) => void;
  isAuthenticated: boolean;
}

export const AuthContext = createContext<AuthContextDataProps>(
  {} as AuthContextDataProps
);

const cookieName = "mundpayToken";

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [authData, setAuthData] = useState<IUser | undefined>();
  const [token, setToken] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(true);
  const [cookies, setCookie, removeCookie] = useCookies([cookieName, "mundpay_login_by_imp"]);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const queryClient = useQueryClient();
  const hasBootstrappedSession = useRef(false);

  async function clearSessionState() {
    setAuthData(undefined);
    setToken("");
    setIsAuthenticated(false);
    localStorage.removeItem("@email");
    localStorage.removeItem("redirectAfterEmailVerification");
    localStorage.removeItem("@token");
    setAuthToken(null);
    useModalStore.getState().resetModals();
    await queryClient.cancelQueries();
    queryClient.clear();

    const useCurrencyStore = (await import('@/store/useCurrencyStore')).default;
    useCurrencyStore.getState().setCurrency('BRL');
  }

  async function loadUserData() {
    const response = await AuthService.userInfo();

    setAuthData(response);
    setIsAuthenticated(true);
    queryClient.setQueryData(["user-info"], response);

    return response;
  }


  const fetchUserData = async () => {
    setLoading(true);
    try {
      await loadUserData();
    } catch (error) {
      await signOut();
    } finally {
      setLoading(false);
    }
  };

  async function signIn(
    email: string,
    password: string
  ): Promise<IAuthResponse | undefined> {
    const auth = await AuthService.login({ email, password });
    if (auth?.token) {
      saveToken(auth.token);
      await fetchUserData();
    }
    return auth;
  }

  async function signInAccount(
    email: string,
    password: string,
    options?: { challenge_id?: string; code?: string },
  ): Promise<any> {
    const response = await AuthService.accountLogin({
      email,
      password,
      challenge_id: options?.challenge_id,
      code: options?.code,
    });

    if (isLoginTwoFactorRequired(response)) {
      return response;
    }

    const token = extractLoginToken(response);
    if (token) {
      saveToken(token);
      await fetchUserData();
    }
    return response;
  }

  async function switchStoreContext(membershipUuid: string): Promise<void> {
    const response = await AuthService.switchStore(membershipUuid);
    const newToken = response?.data?.token;
    if (newToken) {
      await replaceSession(newToken);
    }
  }

  async function signUp(data: {
    terms: boolean;
    name: string;
    email: string;
    password: string;
    password_confirmation: string;
    phone: string;
    phone_prefix: string;
    language_preference?: string;
    invite_token?: string;
  }, referralCode?: string): Promise<IAuthResponse | undefined> {
    const auth = await RegisterService.register(data, referralCode);
    if (auth?.token) {
      saveToken(auth.token);
      await fetchUserData();
    }
    return auth;
  }

  function saveEmail(email: string) {
    localStorage.setItem("@email", JSON.stringify(email?.trim().toLowerCase()));
  }

  function saveToken(newToken: string) {
    const normalizedToken = newToken.replace(/"/g, "");

    setAuthToken(normalizedToken);
    setToken(normalizedToken);
    localStorage.setItem("@token", normalizedToken);
    setCookie(cookieName, normalizedToken, { path: "/", maxAge: 6 * 60 * 60 });
  }

  async function replaceSession(newToken: string) {
    setLoading(true);
    removeCookie(cookieName, { path: "/" });
    removeCookie("mundpay_login_by_imp", { path: "/" });

    await clearSessionState();
    saveToken(newToken);
    setCookie("mundpay_login_by_imp", "true", { path: "/", maxAge: 60 * 60 });

    try {
      await loadUserData();
    } catch (error) {
      removeCookie(cookieName, { path: "/" });
      removeCookie("mundpay_login_by_imp", { path: "/" });
      await clearSessionState();
      throw error;
    } finally {
      setLoading(false);
    }
  }

  async function signOut() {
    removeCookie(cookieName, { path: "/" });
    removeCookie("mundpay_login_by_imp", { path: "/" });
    await clearSessionState();
    window.location.href = "/";
  }
  
  useEffect(() => {
    if (hasBootstrappedSession.current) {
      return;
    }

    hasBootstrappedSession.current = true;

    const savedToken = cookies[cookieName];

    if (savedToken) {
      saveToken(savedToken);
      void fetchUserData();
    } else {
      setLoading(false);
    }
  }, [cookies]);

  return (
    <AuthContext.Provider
      value={{
        authData,
        setAuthData,
        fetchUserData,
        signIn,
        signInAccount,
        signUp,
        signOut,
        replaceSession,
        switchStore: switchStoreContext,
        loading,
        token,
        saveToken,
        saveEmail,
        isAuthenticated,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  const context = useContext(AuthContext);
  return context;
}
